import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileCheck2, FileQuestion, Link2, Loader2 } from "lucide-react";
import { IntakeFormPicker, emailFromClientId } from "@/components/assessment/IntakeFormPicker";

export function LinkedIntakeControl({ assessment, readOnly, onLinked }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const linked = assessment.linked_intake;

  async function link() {
    setBusy(true);
    try {
      const r = await api.post(`/assessments/${assessment.id}/link-intake`, { token });
      const n = r.data?.seeded?.length || 0;
      toast.success("Onboarding form linked", {
        description: n ? `Filled ${n} empty field${n === 1 ? "" : "s"} from it` : "Every matching field was already filled",
      });
      setOpen(false);
      onLinked?.(r.data);
    } catch (e) {
      toast.error("Could not link the form", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="flex items-center gap-1.5 text-xs" data-testid="assessment-linked-intake">
        {linked ? (
          <span className="inline-flex items-center gap-1 text-emerald-800" data-testid="assessment-linked-intake-label">
            <FileCheck2 className="h-3.5 w-3.5" />
            Onboarding: {linked.template_name || "form"} · {linked.client_name} · {linked.submitted_at ? new Date(linked.submitted_at).toLocaleDateString() : ""}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-zinc-500" data-testid="assessment-linked-intake-none">
            <FileQuestion className="h-3.5 w-3.5" /> No onboarding form linked
          </span>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setToken(null); setOpen(true); }}
            className="text-indigo-700 hover:underline underline-offset-2 inline-flex items-center gap-0.5"
            data-testid="assessment-link-intake-btn"
          >
            <Link2 className="h-3 w-3" /> {linked ? "Change" : "Link form"}
          </button>
        )}
      </div>
      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="assessment-link-intake-dialog">
            <DialogHeader>
              <DialogTitle>Link an onboarding form</DialogTitle>
              <DialogDescription className="text-xs">
                Fills every empty field from the chosen form. Anything you&apos;ve already typed is kept.
              </DialogDescription>
            </DialogHeader>
            <IntakeFormPicker
              email={emailFromClientId(assessment.client_id)}
              name={assessment.client_name}
              value={token}
              onChange={setToken}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="assessment-link-intake-cancel">Cancel</Button>
              <Button
                onClick={link}
                disabled={!token || busy}
                className="bg-zinc-950 text-white"
                data-testid="assessment-link-intake-save"
              >
                {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Link &amp; pre-fill
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
